import {Link, Stack, Typography} from '@mui/material'
import {colors, LANGUAGES} from '@/constants'
import React, {useEffect, useState} from 'react'
import useFetching from '@/hooks/useFetching'
import UserService from '@/services/api/User'
import AcceptModal from '@/components/AcceptModal'
import ClassicAcceptModal from '@/components/AcceptModal/Classic'
import ChangePasswordModal from '@/components/AcceptModal/ChangePassword'

import {useAppDispatch, useAppSelector} from '@/hooks/redux'
import useModal from '@/hooks/modal'
import useCopyToClipboard from '@/hooks/useCopyToClipboard'

import {logoutUser, updateUser} from '@/store/actions'
import {selectLanguage, selectUser} from '@/store/selectors'
import EditInput from 'components/UI/input/EditInput'

import {ReactComponent as CopyIcon} from '@/assets/svgs/CopyIcon.svg'
import {ReactComponent as EditIcon} from '@/assets/svgs/Input/EditIcon.svg'
import useAcceptModal from '@/hooks/modalAccept'
import {useTranslation} from 'react-i18next'
import i18n from 'i18next'
import {SET_LANGUAGE} from '@/store/slices'
import {SET_LANGUAGE_STORAGE} from '@/store/slices/storage'
import Select from '@/components/UI/select'
import TransCustom from '@/components/UI/i18next'
import {useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import {ProfileSchema} from '@/helpers/Validators'

const Index = ({navigate}: {navigate: (v: string) => void}) => {
  const {t} = useTranslation()
  const {setAcceptModal} = useAcceptModal()
  const user = useAppSelector(selectUser)
  const language = useAppSelector(selectLanguage)
  const {copyToClipboard} = useCopyToClipboard()
  const dispatch = useAppDispatch()
  const {setModal} = useModal()
  const [email, setEmail] = useState(user!.email)
  const [username, setUsername] = useState(user!.username)
  const [withdrawal, setWithdrawal] = useState(user!.user_address)
  const [internal] = useState(user!.internal_address)

  const {
    register,
    getValues,
    setValue,
    formState: {errors},
  } = useForm({
    mode: 'all',
    resolver: yupResolver(ProfileSchema),
    defaultValues: {userName: user!.username},
  })

  const handleChangeLanguage = (language: string) => {
    dispatch(SET_LANGUAGE(language))
    dispatch(SET_LANGUAGE_STORAGE(language))
    void editProfile({language: language.toUpperCase()})
  }

  useEffect(() => {
    i18n.changeLanguage(language)
  }, [language])

  const [editProfile] = useFetching(async (data) => {
    UserService.editProfile(data)
      .then((res) => {
        if (res.status === 200) {
          dispatch(updateUser())
        } else {
          if (user) {
            setEmail(user.email ? user.email.toLowerCase() : '')
            setUsername(getValues('userName') ? getValues('userName') : '')
            setWithdrawal(user.user_address ? user.user_address : '')
          }
        }
      })
      .catch(() => {
        if (user) {
          setEmail(user.email ? user.email.toLowerCase() : '')
          setValue('userName', user.username || '')
          setWithdrawal(user.user_address ? user.user_address : '')
        }
      })
  })

  if (!user) {
    return null
  }

  const handleLogout = async () => {
    setAcceptModal(
      <AcceptModal
        handleAgree={async () => {
          navigate('/')
          setModal(null)
          setTimeout(() => {
            dispatch(logoutUser())
          }, 200)
        }}>
        <ClassicAcceptModal name={t('1152')} description={t('1153')} />
      </AcceptModal>
    )
  }

  const changePassword = async () => {
    setAcceptModal(
      <AcceptModal confirmText="1235" width="900px" handleAgree={() => {}}>
        <ChangePasswordModal setValue={() => {}} value={''} />
      </AcceptModal>
    )
  }

  return (
    <Stack height="100%" width="80%" justifyContent="space-between">
      <Stack gap="10px">
        <TransCustom i18nKey="1215" variant="h1" color={colors.yellow} />
        <Stack
          direction="row"
          alignItems="center"
          gap="10px"
          sx={{marginBottom: '20px'}}>
          <Typography color={colors.white} fontSize="20px">
            ID: {user.bmc_id}
          </Typography>
          <CopyIcon
            className="svg-first-color-fill pointer"
            onClick={() => copyToClipboard(user.bmc_id)}
          />
        </Stack>
      </Stack>
      <Stack gap="10px" maxWidth="700px">
        <Stack gap="20px">
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              '&:hover svg': {fill: colors.orange},
            }}>
            <TransCustom
              i18nKey="1216"
              variant="body1"
              color={colors.white}
              width="200px"
            />
            <Select
              value={language}
              list={LANGUAGES}
              onChange={(e: any) => handleChangeLanguage(e.target.value)}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              '&:hover svg': {fill: colors.orange},
            }}>
            <TransCustom
              i18nKey="1217"
              color={colors.white}
              fontSize="20px"
              width="200px"
            />
            <EditInput
              schema={{...register('userName')}}
              error={!!errors.userName}
              helperText={errors.userName ? errors.userName.message : ''}
              fullWidth
              onFocusLost={() => editProfile({username: getValues('userName')})}
              onChange={(e: any) => {
                setValue('userName', e.target.value)
              }}
              placeholder={t('1217') || ''}
              variant="standard"
              color="info"
              svgIcon={<EditIcon />}
            />
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              '&:hover svg': {fill: colors.orange},
            }}>
            <TransCustom
              i18nKey="1003"
              variant="body1"
              color={colors.white}
              width="200px"
            />
            <EditInput
              fullWidth
              onFocusLost={() => editProfile({email})}
              onChange={(e: any) => setEmail(e.currentTarget.value)}
              placeholder="Email"
              variant="standard"
              color="info"
              svgIcon={<EditIcon />}
              value={email}
            />
          </Stack>
          <Stack marginTop="40px" direction="row" alignItems="center">
            <Stack direction="column" width="200px">
              <TransCustom
                i18nKey="1218"
                variant="body1"
                color={colors.white}
              />
              <TransCustom i18nKey="1008" variant="body2" color="#73716D" />
            </Stack>
            <Stack direction="row" width="100%" gap="5px">
              <Typography variant="body1">{internal}</Typography>
              <CopyIcon
                className="svg-first-color-fill pointer"
                onClick={() => copyToClipboard(internal || '')}
              />
            </Stack>
          </Stack>
          <Stack
            direction="row"
            alignItems="center"
            sx={{
              '&:hover svg': {fill: colors.orange},
            }}>
            <Stack direction="column" width="200px">
              <TransCustom
                i18nKey="1389"
                variant="body1"
                color={colors.white}
              />
              <TransCustom i18nKey="1008" variant="body2" color="#73716D" />
            </Stack>
            <EditInput
              fullWidth
              onFocusLost={() => editProfile({user_address: withdrawal})}
              onChange={(e: any) => setWithdrawal(e.target.value)}
              placeholder="Withdrawal"
              variant="standard"
              color="info"
              svgIcon={<EditIcon />}
              value={withdrawal}
            />
          </Stack>
        </Stack>
        <Stack direction="row">
          <Stack width="150px" />
          <Stack direction="column" gap="5px">
            <TransCustom color="#FFFDF099" i18nKey="1121" />
            <Stack direction="row">
              <TransCustom color="#FFFDF099" i18nKey="1122" />
              <Link
                href={process.env.REACT_APP_URL_WITHDRAWAL_WALLET}
                marginLeft="5px"
                className="pointer text-hover"
                color={colors.white}
                style={{textDecoration: 'underline'}}
                target="_blank"
                rel="noopener noreferrer">
                <TransCustom i18nKey="1240" />
              </Link>
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack gap="20px" width="fit-content">
        <TransCustom
          i18nKey="1220"
          variant="body1"
          onClick={changePassword}
          className="pointer"
          color={colors.yellow}
          sx={{textDecoration: 'underline'}}
        />
        <TransCustom
          i18nKey="1221"
          variant="body1"
          onClick={handleLogout}
          className="pointer"
          color={colors.yellow}
          sx={{textDecoration: 'underline'}}
        />
      </Stack>
    </Stack>
  )
}

export default Index
